import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Waves, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

export default function Login() {
  const [isRegister, setIsRegister] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isRegister) {
        await register(name, email, password);
        toast.success('Account created');
      } else {
        await login(email, password);
        toast.success('Welcome back!');
      }
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Authentication failed');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1658212180163-2f76c34502e1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBzd2ltbWluZyUyMHBvb2wlMjBiYWNreWFyZCUyMGNvbnN0cnVjdGlvbnxlbnwwfHx8fDE3NzEzNjI0MTd8MA&ixlib=rb-4.1.0&q=85"
          alt="Pool"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F1D32]/90 via-[#0F1D32]/40 to-[#0A1628]/20" />
        <div className="relative z-10 flex flex-col justify-end p-12">
          <div className="flex items-center gap-4 mb-6">
            <img src="/hdh-logo.png" alt="HDH" className="w-16 h-16 object-contain rounded-sm bg-white/10 p-1.5 backdrop-blur" />
            <div>
              <h1 className="text-3xl text-white">HDH Pools & Spas</h1>
              <p className="eyebrow text-sm" style={{color:'#F59E0B'}}>OPERATIONS PLATFORM</p>
            </div>
          </div>
          <p className="text-white/70 text-lg max-w-md leading-relaxed" style={{fontFamily:'Manrope',fontStyle:'normal'}}>
            Build accurate pool bids, manage leads, and track projects — all in one place.
          </p>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-8 bg-[#F8F6F3]">
        <Card className="w-full max-w-md border-slate-200 shadow-sm rounded-sm animate-fade-in">
          <CardHeader className="text-center pb-2">
            <div className="flex items-center justify-center gap-3 mb-4 lg:hidden">
              <img src="/hdh-logo.png" alt="HDH" className="w-12 h-12 object-contain" />
              <span className="text-xl text-[#0F1D32]">HDH Pools & Spas</span>
            </div>
            <CardTitle className="text-2xl text-[#0F1D32]">
              {isRegister ? 'Create Account' : 'Welcome Back'}
            </CardTitle>
            <p className="text-slate-500 text-sm mt-1" style={{fontFamily:'Manrope',fontStyle:'normal'}}>
              {isRegister ? 'Set up your team account' : 'Sign in to your workspace'}
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {isRegister && (
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    required
                    data-testid="register-name-input"
                    className="h-11"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  data-testid="login-email-input"
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    required
                    data-testid="login-password-input"
                    className="h-11 pr-10"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    data-testid="toggle-password-visibility">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-amber-500 hover:bg-amber-600 text-white font-bold uppercase tracking-wider text-xs rounded-sm active:scale-[0.98] transition-all"
                data-testid="login-submit-btn"
              >
                {loading ? 'Please wait...' : isRegister ? 'Create Account' : 'Sign In'}
              </Button>
            </form>
            <div className="mt-4 text-center">
              <button
                onClick={() => setIsRegister(!isRegister)}
                className="text-sm text-sky-600 hover:text-sky-700 font-medium"
                data-testid="toggle-auth-mode-btn"
              >
                {isRegister ? 'Already have an account? Sign in' : "Don't have an account? Create one"}
              </button>
            </div>
            <div className="mt-3 pt-3 border-t border-[#d4e6ef]/50 text-center">
              <a href="/portal/login" className="text-xs font-bold text-[#4a9ac5] hover:text-[#2a6496] transition-colors" data-testid="homeowner-portal-link">
                Homeowner? Access your project portal
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
